import { Alert, Button, Descriptions, Drawer, Input, Popconfirm, Space, Table, Tag, message } from 'antd'
import dayjs from 'dayjs'
import { memo, useCallback, useEffect, useState } from 'react'

import {
  createTeacherSalaryPayout,
  createWiseTransferForSalaryPayout,
  fundTeacherSalaryPayout,
  getTeacherPayoutHistory,
  getTeacherPayoutMethodForAdmin,
  previewTeacherSalaryPayout,
  verifyTeacherPayoutMethod,
} from '~/services/salary-payout'
import {
  ITeacherMonthlySalarySummary,
  ITeacherPayoutMethodRes,
  ITeacherSalaryPayoutPreviewRes,
  ITeacherSalaryPayoutRes,
  IUnsettledSalaryClass,
  TeacherSalaryPayoutStatus,
} from '~/types/salary-payout'

import { ActionRow, DrawerSection } from './style'

interface Props {
  open: boolean
  summary: ITeacherMonthlySalarySummary | null
  year: number
  month: number
  onClose: () => void
  /** Notifies the parent table to refresh after any state change. */
  onRefresh: () => void
  /** Switch the parent month picker (used when admin clicks a history row). */
  onJumpToMonth?: (year: number, month: number) => void
}

const STATUS_COLOR: Record<TeacherSalaryPayoutStatus, string> = {
  QUOTE_CREATED: 'blue',
  TRANSFER_CREATED: 'geekblue',
  FUNDED: 'gold',
  PROCESSING: 'orange',
  OUTGOING_PAYMENT_SENT: 'green',
  BOUNCED_BACK: 'red',
  CANCELLED: 'volcano',
  REFUNDED: 'magenta',
  FAILED: 'red',
}

const fmtMoney = (
  amount: string | number | null | undefined,
  currency: string,
) => (amount == null || amount === '' ? '—' : `${amount} ${currency}`)

const fmtDate = (s: string | null | undefined) =>
  !s ? '—' : dayjs(s).format('YYYY-MM-DD HH:mm:ss')

const UNSETTLED_STATUS_LABEL: Record<IUnsettledSalaryClass['status'], string> = {
  CREATED: '待老师确认',
  CONFIRMED: '已确认',
  UNCONFIRMED: '超时未确认',
}

/** 一个月只发一次、按快照发，所以要等这个月定下来：老师时区里过完了，且每节课都结算了。 */
const PayoutBlockedAlert: React.FC<{ preview: ITeacherSalaryPayoutPreviewRes }> = ({ preview }) => {
  if (preview.payoutBlockedReason === 'MONTH_NOT_ENDED') {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="这个月还没过完，暂时不能发放"
        description={`薪资按老师时区（${preview.teacherTimeZone ?? 'UTC'}）的自然月结算，这个月要到本机时间 ${fmtDate(preview.monthEndsAt)} 才结束。`}
      />
    )
  }
  if (preview.payoutBlockedReason === 'SUMMARY_DEADLINE_PENDING' || preview.payoutBlockedReason === 'MOMENT_REPLY_PENDING') {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="绩效奖金还没定下来，暂时不能发放"
        description={preview.payoutBlockedReason === 'MOMENT_REPLY_PENDING'
          ? '有学生在月底发的 Moment 还在老师的 48 小时回复期内，回复期过后奖金才确定。'
          : '有课堂总结还在课后 3 小时的提交期内，期限过后奖金才确定。'}
      />
    )
  }
  if (preview.payoutBlockedReason === 'UNSETTLED_CLASSES') {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message={`这个月还有 ${preview.unsettledClasses.length} 节课没有结算，暂时不能发放`}
        description={
          <>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preview.unsettledClasses.map((c) => (
                <li key={String(c.appointmentId)}>
                  预约 #{c.appointmentId} · {dayjs(c.classStartTime).format('YYYY-MM-DD HH:mm')}（本机时间）·{' '}
                  {UNSETTLED_STATUS_LABEL[c.status] ?? c.status}
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 4 }}>
              课结束后一般 1 小时内会自动结算；超过这个时间还挂着的，要先把这几节课处理成完成或缺席，再来发放。
            </div>
          </>
        }
      />
    )
  }
  return null
}

interface MonthRow {
  year: number
  month: number
  payout: ITeacherSalaryPayoutRes | null
}

/**
 * Overlay the server's payout history onto a fixed 24-month timeline anchored
 * at the current month. This is what makes "missed months" visible — months
 * the teacher earned for but never got paid show up as "未发放" rows that
 * the admin can click and pay.
 */
const buildMonthTimeline = (history: ITeacherSalaryPayoutRes[]): MonthRow[] => {
  const rows: MonthRow[] = []
  const now = dayjs()
  for (let i = 0; i < 24; i++) {
    const m = now.subtract(i, 'month')
    const y = m.year()
    const mm = m.month() + 1
    const payout = history.find((h) => h.year === y && h.month === mm) || null
    rows.push({ year: y, month: mm, payout })
  }
  return rows
}

const PayoutDetailDrawer: React.FC<Props> = ({
  open,
  summary,
  year,
  month,
  onClose,
  onRefresh,
  onJumpToMonth,
}) => {
  const [preview, setPreview] = useState<ITeacherSalaryPayoutPreviewRes | null>(null)
  const [payoutMethod, setPayoutMethod] = useState<ITeacherPayoutMethodRes | null>(null)
  const [history, setHistory] = useState<ITeacherSalaryPayoutRes[]>([])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<
    'verify' | 'quote' | 'transfer' | 'fund' | null
  >(null)
  const [reference, setReference] = useState<string>('')
  const [sourceCurrency, setSourceCurrency] = useState<string>('')

  const teacherId = summary?.teacherId

  const refreshAll = useCallback(async () => {
    if (!teacherId) return
    setLoading(true)
    try {
      const [previewRes, methodRes, historyRes] = await Promise.all([
        previewTeacherSalaryPayout(teacherId, year, month),
        getTeacherPayoutMethodForAdmin(teacherId).catch(() => ({ data: null })),
        getTeacherPayoutHistory(teacherId).catch(() => ({ data: [] })),
      ])
      setPreview(previewRes.data)
      setPayoutMethod(methodRes.data ?? null)
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : [])
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
      message.error(msg || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [teacherId, year, month])

  useEffect(() => {
    if (open && teacherId) {
      setReference('')
      setSourceCurrency('')
      refreshAll()
    } else {
      setPreview(null)
      setPayoutMethod(null)
      setHistory([])
    }
  }, [open, teacherId, refreshAll])

  const payout: ITeacherSalaryPayoutRes | null = preview?.existingPayout ?? null
  const status = payout?.status

  // Verify button is only meaningful when there's actually a submitted
  // method that hasn't been approved yet.
  const canVerify = !!payoutMethod && !payoutMethod.isVerified
  const canCreateQuote =
    !!payoutMethod?.isVerified && !!preview && !preview.payoutBlockedReason
    && (!status || ['QUOTE_CREATED', 'FAILED', 'CANCELLED', 'BOUNCED_BACK', 'REFUNDED'].includes(status))
    // 批量转账里的老师在页面顶部的批次里处理（后端也会拒绝）
    && !(status === 'QUOTE_CREATED' && !!payout?.batchId)
  const canCreateTransfer = !!payout && status === 'QUOTE_CREATED' && !payout.batchId
  // 批量转账只能整批在 Wise 付款，不单笔出资
  const inBatch = !!payout?.batchId
  const canFund = !!payout && status === 'TRANSFER_CREATED' && !inBatch
  const isFinalGood = status === 'OUTGOING_PAYMENT_SENT' || status === 'FUNDED'

  const handleVerify = useCallback(async () => {
    if (!teacherId) return
    setActionLoading('verify')
    try {
      await verifyTeacherPayoutMethod(teacherId)
      message.success('Payout method verified')
      onRefresh()
      await refreshAll()
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
      message.error(msg || 'Verify failed')
    } finally {
      setActionLoading(null)
    }
  }, [teacherId, onRefresh, refreshAll])

  const handleCreateQuote = useCallback(async () => {
    if (!teacherId) return
    setActionLoading('quote')
    try {
      await createTeacherSalaryPayout(teacherId, year, month, {
        sourceCurrency: sourceCurrency.trim() || undefined,
        reference: reference.trim() || undefined,
      })
      message.success('Wise quote created')
      onRefresh()
      await refreshAll()
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
      message.error(msg || 'Create quote failed')
    } finally {
      setActionLoading(null)
    }
  }, [teacherId, year, month, sourceCurrency, reference, onRefresh, refreshAll])

  const handleCreateTransfer = useCallback(async () => {
    if (!payout) return
    setActionLoading('transfer')
    try {
      await createWiseTransferForSalaryPayout(
        payout.id,
        reference.trim() || undefined,
      )
      message.success('Wise transfer created')
      onRefresh()
      await refreshAll()
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
      message.error(msg || 'Create transfer failed')
    } finally {
      setActionLoading(null)
    }
  }, [payout, reference, onRefresh, refreshAll])

  const handleFund = useCallback(async () => {
    if (!payout) return
    setActionLoading('fund')
    try {
      await fundTeacherSalaryPayout(payout.id)
      message.success('Transfer funded')
      onRefresh()
      await refreshAll()
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
      message.error(msg || 'Fund failed')
    } finally {
      setActionLoading(null)
    }
  }, [payout, onRefresh, refreshAll])

  return (
    <Drawer
      title={summary ? `${summary.teacherName} · ${year}-${String(month).padStart(2, '0')} 工资发放` : ''}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      loading={loading}
    >
      {!summary ? null : (
        <>
          <DrawerSection>
            <h4>状态</h4>
            <Space size="small" wrap>
              {/* Read from re-fetched payoutMethod, NOT the stale row summary —
                  the drawer can switch month via history click and the original
                  summary refers to the month the admin first clicked. */}
              {payoutMethod?.isVerified ? (
                <Tag color="green">收款方式已审核</Tag>
              ) : (
                <Tag color="orange">收款方式未审核</Tag>
              )}
              {status ? (
                <Tag color={STATUS_COLOR[status]}>{status.replace(/_/g, ' ')}</Tag>
              ) : (
                <Tag>未发放</Tag>
              )}
              {isFinalGood && <Tag color="green">钱已寄出</Tag>}
              {inBatch && <Tag color="blue">批量转账</Tag>}
            </Space>
            {inBatch && status === 'TRANSFER_CREATED' && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                message="这笔在本月的批量转账里"
                description="不用单独处理：在页面顶部锁定批次后，去 Wise 对整个批次付一次款即可。"
              />
            )}
            {payout?.errorMessage && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 8 }}
                message={payout.errorMessage}
              />
            )}
          </DrawerSection>

          <DrawerSection>
            <h4>收款方式详情（待审核内容）</h4>
            {!payoutMethod ? (
              <Alert
                type="warning"
                showIcon
                message="老师还没提交收款方式"
                description="请先让老师在注册页或仪表盘的 Payout Method 模块填写收款信息。"
              />
            ) : (
              <>
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="路由（country / currency / method）">
                    {payoutMethod.country} · {payoutMethod.currency} · {payoutMethod.method}
                  </Descriptions.Item>
                  <Descriptions.Item label="账户持有人">
                    {payoutMethod.accountHolderName || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="审核状态">
                    {payoutMethod.isVerified ? (
                      <Tag color="green">已审核</Tag>
                    ) : (
                      <Tag color="orange">未审核</Tag>
                    )}
                    {payoutMethod.verificationDate && (
                      <span style={{ color: '#9ca3af', marginLeft: 8 }}>
                        {fmtDate(payoutMethod.verificationDate)}
                      </span>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="提交时间">
                    {fmtDate(payoutMethod.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="最近修改">
                    {fmtDate(payoutMethod.updatedAt)}
                  </Descriptions.Item>
                </Descriptions>
                <h4 style={{ marginTop: 12 }}>表单字段（老师填写的内容）</h4>
                {Object.keys(payoutMethod.details).length === 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    message="details 为空"
                    description="老师没填写任何字段，审核前请联系老师补全。"
                  />
                ) : (
                  <Descriptions size="small" column={1} bordered>
                    {Object.entries(payoutMethod.details).map(([k, v]) => (
                      <Descriptions.Item key={k} label={k}>
                        <span style={{
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          wordBreak: 'break-all',
                        }}>
                          {v || '—'}
                        </span>
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                )}
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                  message="审核要点"
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      <li>"账户持有人"是否与老师真实姓名匹配</li>
                      <li>邮箱 / IBAN / 账号格式是否正确，与老师 Wise 注册邮箱一致</li>
                      <li>地址国家与所选路由 country 是否对得上</li>
                      <li>核对无误后点 ① 通过审核；不对则联系老师在前端重填（重填后会自动作废审核）</li>
                    </ul>
                  }
                />
              </>
            )}
          </DrawerSection>

          <DrawerSection>
            <h4>{year}-{String(month).padStart(2, '0')} 薪资统计</h4>
            {/* Reads from `preview` (refreshed per month) instead of `summary`
                (frozen at first row click), so switching month via the
                history click correctly reflects the new month's stats. */}
            {preview ? (
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="货币">{preview.salaryCurrency}</Descriptions.Item>
                <Descriptions.Item label="每节课">
                  {preview.ratePerClass} {preview.salaryCurrency}
                </Descriptions.Item>
                <Descriptions.Item label="结算课数">
                  {preview.totalClassCount}（完成 {preview.completedClassCount} / 学生缺席 {preview.studentAbsentCount}）
                </Descriptions.Item>
                <Descriptions.Item label="老师缺席">
                  {preview.teacherAbsentCount}（扣 {preview.teacherAbsentDeduction} {preview.salaryCurrency}）
                </Descriptions.Item>
                <Descriptions.Item label="净薪资">
                  <strong>{preview.netSalary} {preview.salaryCurrency}</strong>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert type="info" showIcon message="加载中…" />
            )}
          </DrawerSection>

          {payout && (
            <DrawerSection>
              <h4>Wise 快照</h4>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="发放时每节课 / 发放金额">
                  {fmtMoney(payout.ratePerClass, payout.salaryCurrency)} /{' '}
                  {fmtMoney(payout.salaryAmount, payout.salaryCurrency)}
                </Descriptions.Item>
                <Descriptions.Item label="Quote ID">
                  {payout.wiseQuoteId || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Quote 过期时间">
                  {fmtDate(payout.wiseQuoteExpiresAt)}
                </Descriptions.Item>
                <Descriptions.Item label="付款方式">
                  {payout.wisePayInType || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="源币种 → 目标币种">
                  {payout.sourceCurrency} → {payout.targetCurrency}
                </Descriptions.Item>
                <Descriptions.Item label="源金额 / 目标金额">
                  {fmtMoney(payout.wiseSourceAmount, payout.sourceCurrency)} /{' '}
                  {fmtMoney(payout.wiseTargetAmount, payout.targetCurrency)}
                </Descriptions.Item>
                <Descriptions.Item label="手续费 / 汇率">
                  {fmtMoney(payout.wiseFee, payout.sourceCurrency)} /{' '}
                  {payout.wiseRate ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Recipient ID">
                  {payout.wiseRecipientId ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Transfer ID">
                  {payout.wiseTransferId ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="发放方式">
                  {payout.batchId ? `批量转账（批次 #${payout.batchId}）` : payout.wiseTransferId ? '单笔' : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Wise 状态">
                  {payout.wiseTransferStatus || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="出资状态">
                  {payout.wiseFundingStatus || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="customerTransactionId">
                  {payout.customerTransactionId}
                </Descriptions.Item>
                <Descriptions.Item label="出资时间">
                  {fmtDate(payout.fundedAt)}
                </Descriptions.Item>
                <Descriptions.Item label="最近 webhook">
                  {fmtDate(payout.wiseLastEventAt)}
                </Descriptions.Item>
              </Descriptions>
            </DrawerSection>
          )}

          <DrawerSection>
            <h4>近 24 个月发放情况</h4>
            <Table
              size="small"
              rowKey={(row) => `${row.year}-${row.month}`}
              dataSource={buildMonthTimeline(history)}
              pagination={false}
              scroll={{ y: 320 }}
              onRow={(row) => ({
                style: {
                  cursor: onJumpToMonth ? 'pointer' : 'default',
                  background:
                    row.year === year && row.month === month
                      ? '#fffbe6'   // 当前选中月：浅黄
                      : undefined,
                },
                onClick: () => {
                  if (onJumpToMonth) onJumpToMonth(row.year, row.month)
                },
              })}
              columns={[
                {
                  title: '月份',
                  key: 'ym',
                  width: 90,
                  render: (_, row) =>
                    `${row.year}-${String(row.month).padStart(2, '0')}`,
                },
                {
                  title: '状态',
                  key: 'status',
                  width: 130,
                  render: (_, row) => {
                    if (!row.payout) {
                      return <Tag>未发放</Tag>
                    }
                    return (
                      <Tag color={STATUS_COLOR[row.payout.status]} style={{ marginRight: 0 }}>
                        {row.payout.status.replace(/_/g, ' ')}
                      </Tag>
                    )
                  },
                },
                {
                  title: '金额',
                  key: 'amount',
                  align: 'right',
                  render: (_, row) =>
                    row.payout
                      ? `${row.payout.salaryAmount} ${row.payout.salaryCurrency}`
                      : <span style={{ color: '#9ca3af' }}>—</span>,
                },
                {
                  title: '出资时间',
                  key: 'fundedAt',
                  width: 150,
                  render: (_, row) => fmtDate(row.payout?.fundedAt ?? null),
                },
                {
                  title: 'Wise',
                  key: 'wiseRef',
                  width: 110,
                  render: (_, row) =>
                    row.payout?.wiseTransferId ? (
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        #{row.payout.wiseTransferId}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    ),
                },
              ]}
            />
            {onJumpToMonth && (
              <small style={{ color: '#6b7280', fontSize: 12, display: 'block', marginTop: 8 }}>
                点击任意月份切换到该月。"未发放"的月份可以补发 — 操作流程跟本月完全一样。
              </small>
            )}
          </DrawerSection>

          <DrawerSection>
            <h4>参数</h4>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                placeholder="reference（≤35 字，留空则自动生成 Salary YYYY-MM）"
                value={reference}
                maxLength={35}
                onChange={(e) => setReference(e.target.value)}
                disabled={!!actionLoading}
              />
              <Input
                placeholder="sourceCurrency（留空 = 自动选择，优先与目标同币）"
                value={sourceCurrency}
                onChange={(e) => setSourceCurrency(e.target.value.toUpperCase())}
                disabled={!!actionLoading}
              />
            </Space>
          </DrawerSection>

          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="单笔发放：第 ④ 步在 prod 上需要在 wise.com 手动完成（每月发工资请优先用页面顶部的批量转账，只需付一次款）"
            description={
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                Wise 规定：用 Personal API token 的企业号，只有美国、加拿大、澳大利亚、新西兰、新加坡、马来西亚的账户能通过 API
                出资。我们的企业号（Shaokai Services）在日本，API 出资会被 Wise 返回{' '}
                <code>403 forbidden</code>（预期行为，非 bug，跟 SCA 签名无关），必须登录 Wise 手动出资。
                <ol style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>① ② ③ 步骤照常点 — 创建 quote 和 transfer 不需要手动批准。</li>
                  <li>
                    第 ④ 步：登录{' '}
                    <a href="https://wise.com" target="_blank" rel="noopener noreferrer">
                      wise.com
                    </a>{' '}
                    →切到业务账户 → 找到 transfer
                    {payout?.wiseTransferId ? (
                      <>
                        {' '}<b style={{ fontFamily: 'monospace' }}>#{payout.wiseTransferId}</b>
                      </>
                    ) : (
                      <> （上一步生成的 ID）</>
                    )}{' '}
                    → 选「您的账户 / BALANCE」→ 输密码 + App 批准。
                  </li>
                  <li>fund 完成后 webhook 会自动把状态同步成 PROCESSING / FUNDED — 不用回来再点 ④。</li>
                </ol>
                <div style={{ marginTop: 8, color: '#9ca3af' }}>
                  ④ 按钮只在 sandbox 调试时有用，prod 直接点会拿到 <code>HTTP 403</code>。依据：{' '}
                  <a
                    href="https://docs.wise.com/guides/developer/auth-and-security/personal-api-token"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Wise 文档 · Personal API tokens
                  </a>
                  。
                </div>
              </div>
            }
          />

          {preview && (!status || status === 'QUOTE_CREATED' || status === 'FAILED') && (
            <PayoutBlockedAlert preview={preview} />
          )}

          <ActionRow>
            <Button
              onClick={handleVerify}
              loading={actionLoading === 'verify'}
              disabled={!canVerify || !!actionLoading}
            >
              ① 审核收款方式
            </Button>
            <Popconfirm
              title="创建 Wise 报价？"
              description={
                status === 'FAILED'
                  ? '将覆盖已 FAILED 的报价行，重新生成 customerTransactionId。'
                  : status === 'QUOTE_CREATED'
                    ? '将覆盖现有报价（重新锁汇率）。'
                    : '将根据本月净薪资创建报价并缓存收款人。'
              }
              okText="确认"
              cancelText="取消"
              onConfirm={handleCreateQuote}
              disabled={!canCreateQuote || !!actionLoading}
            >
              <Button
                type="primary"
                loading={actionLoading === 'quote'}
                disabled={!canCreateQuote || !!actionLoading}
              >
                ② 创建报价
              </Button>
            </Popconfirm>
            <Popconfirm
              title="创建 Wise Transfer？"
              description="此步骤会调用 POST /v1/transfers 但还不会扣钱。"
              okText="确认"
              cancelText="取消"
              onConfirm={handleCreateTransfer}
              disabled={!canCreateTransfer || !!actionLoading}
            >
              <Button
                loading={actionLoading === 'transfer'}
                disabled={!canCreateTransfer || !!actionLoading}
              >
                ③ 建 Transfer
              </Button>
            </Popconfirm>
            <Popconfirm
              title="API 出资（prod 会 403）"
              description={
                <div style={{ maxWidth: 320 }}>
                  此按钮调用 <code>POST /payments</code> 直接 API 出资。
                  <br />
                  <b>prod 不支持</b>—会返回 403。请按上方提示去 wise.com 手动 fund。
                  <br />
                  （日本企业号不支持 API 出资，此按钮只在 sandbox 有用）
                </div>
              }
              okText="仍要尝试"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={handleFund}
              disabled={!canFund || !!actionLoading}
            >
              <Button
                danger
                loading={actionLoading === 'fund'}
                disabled={!canFund || !!actionLoading}
              >
                ④ 出资发钱
              </Button>
            </Popconfirm>
          </ActionRow>
        </>
      )}
    </Drawer>
  )
}

export default memo(PayoutDetailDrawer)
